import { type ComponentConfig } from "@puckeditor/core";
import { BenefitsList, type BenefitsListProps } from "./benefits-list";
import { BenefitsListFields } from "./benefits-list.fields";
import { RegenerableTextField } from "../editor/RegenerableTextField";

export const BenefitsListConfig: ComponentConfig<BenefitsListProps> = {
  // Display label names the structure, not one use: an icon list serves
  // benefits, credentials, inclusions, or values equally. The registry key
  // stays BenefitsList — it's persisted in every page's Puck data.
  label: "Icon List",
  fields: {
    heading: {
      ...BenefitsListFields.heading,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="benefits.heading"
          label="Heading"
          multiline={false}
          fieldMeta={field?.metadata}
        />
      ),
    },
    subheading: {
      ...BenefitsListFields.subheading,
      contentEditable: true,
      render: ({ value, onChange, field }) => (
        <RegenerableTextField
          value={value || ""}
          onChange={onChange}
          namespace="benefits.subheading"
          label="Subheading"
          multiline={true}
          fieldMeta={field?.metadata}
        />
      ),
    },
    layout: BenefitsListFields.layout,
    iconStyle: BenefitsListFields.iconStyle,
    columns: BenefitsListFields.columns,
    benefits: BenefitsListFields.benefits as any,
    accentColor: BenefitsListFields.accentColor,
    backgroundColor: BenefitsListFields.backgroundColor,
  },
  /**
   * Columns only apply to the grid layout, and the per-item icon is only read when the icon
   * style is "custom" — otherwise every item takes the one shared icon.
   */
  resolveFields: (data, { fields }) => {
    const props = (data.props || {}) as { layout?: string; iconStyle?: string };
    const visible = { ...(fields as Record<string, any>) };

    if (props.layout !== "grid") {
      delete visible.columns;
    }

    if (props.iconStyle !== "custom" && visible.benefits?.arrayFields?.icon) {
      const { icon: _icon, ...arrayFields } = visible.benefits.arrayFields;
      visible.benefits = { ...visible.benefits, arrayFields };
    }

    return visible as typeof fields;
  },
  defaultProps: {
    heading: "Why Choose Us",
    subheading:
      "Experience the difference of working with true professionals who care about quality.",
    layout: "grid",
    iconStyle: "check",
    columns: "3",
    benefits: [
      { text: "Licensed & Insured Professionals" },
      { text: "Upfront, Transparent Pricing" },
      { text: "Same-Day Service Available" },
      { text: "100% Satisfaction Guarantee" },
      { text: "24/7 Emergency Response" },
      { text: "Locally Owned & Operated" },
    ],
  },
  render: ({ id, puck, editMode, ...props }) => {
    return <BenefitsList id={id} {...props} />;
  },
};
